import { useSearchParams } from "react-router-dom";

import { type ColumnFiltersState, type OnChangeFn, functionalUpdate } from "@tanstack/react-table";

import { withParams } from "@/lib/url-params";

/** Ustun `id` si ↔ URL parametri. */
export interface FilterBinding {
  columnId: string;
  param: string;
  /** Ruxsat etilgan qiymatlar: URL ni odam ham tahrirlaydi, begona qiymat filtr emas. */
  allowed?: readonly string[];
  /**
   * Matn filtri tarixga yozilmaydi (`replace`): har bir to'xtalish uchun yozuv
   * qolsa, "orqaga" tugmasi avvalgi ekranga emas, qidiruvning oldingi harfiga
   * qaytarardi. Tanlov esa — odamning ataylab qilgan ishi, u tarixga tushadi.
   */
  replace?: boolean;
}

const asValue = (value: unknown): string | undefined =>
  value === undefined || value === null || value === "" ? undefined : String(value);

/**
 * Ustun sarlavhasidagi filtrlarni manzil bilan bog'laydi.
 *
 * TanStack ning ichki `columnFilters` holati sahifa yangilanganda yo'qoladi
 * va havolaga tushmaydi. Shuning uchun URL — yagona manba, jadval uni faqat
 * aks ettiradi. Faqat O'ZGARGAN parametr yoziladi; filtr o'zgarsa sahifa
 * birinchisiga qaytadi (`withParams`).
 */
export function useUrlColumnFilters(bindings: readonly FilterBinding[]): {
  columnFilters: ColumnFiltersState;
  onColumnFiltersChange: OnChangeFn<ColumnFiltersState>;
} {
  const [params, setParams] = useSearchParams();

  const columnFilters: ColumnFiltersState = bindings.flatMap(binding => {
    const value = asValue(params.get(binding.param));
    if (value === undefined) return [];
    if (binding.allowed && !binding.allowed.includes(value)) return [];
    return [{ id: binding.columnId, value }];
  });

  const onColumnFiltersChange: OnChangeFn<ColumnFiltersState> = updater => {
    const next = functionalUpdate(updater, columnFilters);
    const changes: Record<string, string | undefined> = {};
    let replace = true;

    // HAR BIR bog'langan parametr tekshiriladi: olib tashlangan filtr `next`
    // da umuman bo'lmaydi, va uni yozmasak manzilda qolib ketardi.
    for (const binding of bindings) {
      const current = asValue(columnFilters.find(item => item.id === binding.columnId)?.value);
      const wanted = asValue(next.find(item => item.id === binding.columnId)?.value);
      if (current === wanted) continue;
      changes[binding.param] = wanted;
      if (!binding.replace) replace = false;
    }

    if (Object.keys(changes).length > 0) {
      setParams(prev => withParams(prev, changes), { replace });
    }
  };

  return { columnFilters, onColumnFiltersChange };
}
